import {
  PostCreateDto,
  PostUpdateDto,
  Pagination,
  PostRequestRecruiterDto,
  ApplicationUpdateDto,
  PaginationQuery,
} from '@libs/dto';
import { Post, PostRequest, Application } from '@libs/models';
import { ApplicationSource, PaginatedQueryParams, PostRequestStatus } from '@libs/types';

import { api } from '../common.api';

export const postApi = api.injectEndpoints({
  endpoints: (build) => ({
    postCreate: build.mutation<Post, PostCreateDto>({
      query: (body) => ({
        url: '/post',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Post'],
    }),
    postIndex: build.query<Pagination<Post>, PaginationQuery>({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: '/post',
        method: 'GET',
        params: { page, limit },
      }),
      providesTags: ['Post'],
    }),
    postSearch: build.query<Pagination<Post>, PaginatedQueryParams>({
      query: ({ q, page = 1, limit = 10 }) => ({
        url: '/post/search',
        method: 'GET',
        params: { q, page, limit },
      }),
      providesTags: ['Post'],
    }),
    postView: build.query<Post, number>({
      query: (id) => ({
        url: `/post/${id}`,
        method: 'GET',
      }),
      providesTags: ['Post'],
    }),
    postUpdateStartDate: build.mutation<Post, PostUpdateDto>({
      query: (body) => ({
        url: `/post/${body.id}/start-date`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Post'],
    }),
    postUpdateEndDate: build.mutation<Post, PostUpdateDto>({
      query: (body) => ({
        url: `/post/${body.id}/end-date`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Post'],
    }),
    postStart: build.mutation<Post, number>({
      query: (id) => ({
        url: `/post/${id}/start`,
        method: 'PUT',
      }),
      invalidatesTags: ['Post'],
    }),
    postClose: build.mutation<Post, number>({
      query: (id) => ({
        url: `/post/${id}/close`,
        method: 'PUT',
      }),
      invalidatesTags: ['Post'],
    }),
    postDelete: build.mutation<Post, number>({
      query: (id) => ({
        url: `/post/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Post'],
    }),
    postRecent: build.query<Post[], void>({
      query: () => ({
        url: '/post/recent',
        method: 'GET',
      }),
      providesTags: ['Post'],
    }),
    postRenew: build.mutation<Post, { id: number; renew: boolean }>({
      query: ({ id, renew }) => ({
        url: `/post/${id}/renew`,
        method: 'PUT',
        body: { renew },
      }),
      invalidatesTags: ['Post'],
    }),
    postRequestRecruiter: build.mutation<PostRequest, PostRequestRecruiterDto>({
      query: (body) => ({
        url: '/post/request-recruiter',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PostRequest'],
    }),
    postRequestCountPending: build.query<number, void>({
      query: () => ({
        url: '/post/request-count-pending',
        method: 'GET',
      }),
      providesTags: ['PostRequest'],
    }),
    postRequestIndex: build.query<Pagination<PostRequest>, PaginationQuery>({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: '/post/request',
        method: 'GET',
        params: { page, limit },
      }),
      providesTags: ['PostRequest'],
    }),
    postRequestSearch: build.query<Pagination<PostRequest>, PaginationQuery>({
      query: ({ q, page = 1, limit = 10 }) => ({
        url: '/post/request/search',
        method: 'GET',
        params: { q, page, limit },
      }),
      providesTags: ['PostRequest'],
    }),
    postRequestUpdateStatus: build.mutation<PostRequest, { id: number; status: PostRequestStatus }>({
      query: ({ id, status }) => ({
        url: `/post/request/${id}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['PostRequest'],
    }),
    employerApplications: build.query<Pagination<Application>, PaginationQuery>({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: '/post/employer/applications',
        method: 'GET',
        params: { page, limit },
      }),
      providesTags: ['Application'],
    }),
    employerApplicationsSearch: build.query<Pagination<Application>, PaginationQuery>({
      query: ({ q, page = 1, limit = 10 } = {}) => ({
        url: '/post/employer/applications/search',
        method: 'GET',
        params: { q, page, limit },
      }),
      providesTags: ['Application'],
    }),
    postApplications: build.query<Pagination<Application>, PaginationQuery>({
      query: ({ id, page = 1, limit = 10 } = {}) => ({
        url: `/post/${id}/applications`,
        method: 'GET',
        params: { page, limit },
      }),
      providesTags: ['Application'],
    }),
    postApplicationsSearch: build.query<Pagination<Application>, PaginationQuery>({
      query: ({ id, q = '', page = 1, limit = 10 } = {}) => ({
        url: `/post/${id}/applications/search`,
        method: 'GET',
        params: { q, page, limit },
      }),
      providesTags: ['Application'],
    }),
    apply: build.mutation<Application, { postId: number; source: ApplicationSource }>({
      query: ({ postId, source }) => ({
        url: `/post/${postId}/apply`,
        method: 'POST',
        body: { source },
      }),
      invalidatesTags: ['Application', 'Jobseeker', 'Post'],
    }),
    unapply: build.mutation<Application, number>({
      query: (postId) => ({
        url: `/post/${postId}/unapply`,
        method: 'POST',
      }),
      invalidatesTags: ['Application', 'Jobseeker', 'Post'],
    }),
    save: build.mutation<{ message: string }, number>({
      query: (postId) => ({
        url: `/post/${postId}/save`,
        method: 'POST',
      }),
      invalidatesTags: ['Jobseeker', 'Post'],
    }),
    hasApplied: build.query<{ applied: boolean; application?: Application }, number>({
      query: (postId) => `/post/${postId}/check-application`,
      providesTags: ['Application'],
    }),
    updateApplication: build.mutation<Application, { id: number; data: ApplicationUpdateDto }>({
      query: ({ id, data }) => ({
        url: `/post/applications/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Application', 'Post'],
    }),
    applicationView: build.query<Application, number>({
      query: (id) => `/post/applications/${id}`,
      providesTags: ['Application'],
    }),
    resendApplicationNotification: build.mutation<void, number>({
      query: (id) => ({
        url: `/post/applications/${id}/resend-notification`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  usePostCreateMutation,
  usePostIndexQuery,
  usePostSearchQuery,
  usePostViewQuery,
  usePostUpdateStartDateMutation,
  usePostUpdateEndDateMutation,
  usePostStartMutation,
  usePostCloseMutation,
  usePostDeleteMutation,
  usePostRecentQuery,
  usePostRequestRecruiterMutation,
  usePostRequestCountPendingQuery,
  usePostRequestIndexQuery,
  usePostRequestSearchQuery,
  useLazyPostRequestSearchQuery,
  usePostRequestUpdateStatusMutation,
  useEmployerApplicationsQuery,
  useEmployerApplicationsSearchQuery,
  usePostApplicationsQuery,
  usePostApplicationsSearchQuery,
  useApplyMutation,
  useUnapplyMutation,
  useSaveMutation,
  useHasAppliedQuery,
  useUpdateApplicationMutation,
  useApplicationViewQuery,
  useResendApplicationNotificationMutation,
  useLazyPostApplicationsQuery,
  usePostRenewMutation,
} = postApi;
